/* global window */
/**
 * Small ``matchMedia`` hook for tailwind-style breakpoint switches in
 * React state (e.g. choosing between a side panel and a bottom drawer).
 *
 * SSR-safe (returns ``false`` while ``window`` is undefined), subscribes
 * to ``change`` events with cleanup, and re-runs when ``query`` changes
 * so the hook can be driven by a prop.
 */
import * as React from "react";

export function useMediaQuery(query: string): boolean {
  const get = React.useCallback(
    () => (typeof window !== "undefined" ? window.matchMedia(query).matches : false),
    [query],
  );
  const [matches, setMatches] = React.useState<boolean>(get);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    // Initial sync in case the query changed between render and effect.
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}
